import type { Theme } from '@material-ui/core'
import { Fade, makeStyles } from '@material-ui/core'

/**
 * The dropdown look of the estimate/invoice redesign prototype, shared by every menu in the
 * `estimateNext` editor (ENG-1919).
 *
 * The prototype draws all of its menus from one surface primitive: a 12px-radius white card with a
 * hairline border and a three-layer shadow, holding 6px-inset rows that each round to 8px and take a
 * wash on hover. It drops in over 0.14s, fading up from 4px above its resting place. Material's own menu is square-cornered, full-bleed, and anchors *over* its trigger,
 * so a call site opting in needs all three of the pieces below — the classes, the placement, and the
 * gap.
 *
 * Values are the prototype's, mapped onto the Brand Neutral scale (`#c6cedc` = P85, `#f0f3f7` = P97,
 * `#96a5b9` ≈ P70), then recoloured to the ENG-2094 spec: row labels in the text colour (P35) rather
 * than the prototype's plain black, the group rule in the P90 divider, and row glyphs in P40.
 *
 * Use it through `ActionButton`'s `nextMenu` prop, or by hand for a `Menu` that has no ActionButton
 * (see `QuoteNextActionsMenu`).
 */
export const useNextMenuStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      '@keyframes nextMenuIn': {
        from: { opacity: 0, transform: 'translateY(-4px)' },
        to: { opacity: 1, transform: 'translateY(0)' }
      },
      // The menu card. `minWidth` is the prototype's 200; the max keeps a long label (e.g. "Convert
      // to Line Item") from stretching the card past its 280 ceiling.
      paper: {
        minWidth: 200,
        maxWidth: 280,
        backgroundColor: neutral.White,
        border: `0.5px solid ${neutral.P85}`,
        borderRadius: 12,
        boxShadow: '0 1px 2px rgba(20,30,50,0.06), 0 6px 16px rgba(20,30,50,0.08), 0 24px 56px rgba(20,30,50,0.16)',
        // The entrance. The placements below swap Material's Grow for a Fade, whose opacity-only
        // inline style leaves `transform` free for this.
        animation: '$nextMenuIn 0.14s ease-out forwards'
      },
      // The prototype's 6px gap to the trigger, for a menu that drops below one. `anchorOrigin`
      // alone puts the card flush against the trigger's bottom edge.
      paperBelow: {
        marginTop: 6
      },
      // Same gap for a submenu that flies out to the right of its parent row.
      paperRight: {
        marginLeft: 6
      },
      // 6px all round, so the first/last row's rounded corner sits inside the card's own radius
      // instead of against it.
      list: {
        padding: 6
      },
      // One row. The icon is pulled to the prototype's 16px at an 8px gap — Material's MenuItem ships
      // a 24px glyph with a 16px gutter, which reads as a much heavier list.
      item: {
        minHeight: 36,
        padding: '8px 10px',
        borderRadius: 8,
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 1.4,
        color: theme.palette.text.primary,
        '&:hover': {
          backgroundColor: neutral.P97
        },
        // width/height as well as fontSize: a plain inlined `<svg>` (as against a MUI `SvgIcon`) has
        // no intrinsic size and would otherwise stretch to the full width of the row.
        '& > svg:first-child, & > .MuiSvgIcon-root:first-child': {
          fontSize: theme.typography.pxToRem(16),
          width: '1em',
          height: '1em',
          flexShrink: 0,
          marginRight: theme.spacing(1),
          color: neutral.P40
        },
        // Touch targets get the prototype's taller row rather than the desktop 36.
        [theme.breakpoints.down('xs')]: {
          minHeight: 44,
          padding: '11px 10px',
          fontSize: theme.typography.pxToRem(15)
        }
      },
      // Inset hairline between groups, rather than Material's full-bleed rule.
      divider: {
        margin: theme.spacing(0.75, 0.75),
        backgroundColor: neutral.P90
      },
      // A row that opens a submenu: label left, caret hard right.
      submenuItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: theme.spacing(2)
      },
      // The submenu row whose flyout is open: it keeps the hover wash while the pointer is in the
      // flyout, so the two read as one path (ENG-1920).
      itemActive: {
        backgroundColor: neutral.P97,
        '&:hover': {
          backgroundColor: neutral.P97
        }
      },
      submenuArrow: {
        fontSize: theme.typography.pxToRem(16),
        color: neutral.P70
      }
    }
  },
  { name: 'NextMenu' }
)

/**
 * Material's Grow scales the card from its corner through an inline `transform`, which would fight
 * the `paper` class's own translate-in. A Fade at the same 0.14s only touches opacity.
 */
const NEXT_MENU_TRANSITION = {
  TransitionComponent: Fade,
  transitionDuration: 140
} as const

/**
 * Placement for a menu hanging off a trigger: below it, right edges aligned.
 *
 * Material anchors a menu over its trigger by default (it lines the selected row up with the
 * button), which is what ENG-1921 reports as the Add dropdown covering the + Add button. Pinning
 * `getContentAnchorEl` to null is what lets `anchorOrigin` take effect at all.
 */
export const NEXT_MENU_PLACEMENT = {
  ...NEXT_MENU_TRANSITION,
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
  transformOrigin: { vertical: 'top', horizontal: 'right' }
} as const

/**
 * Placement for a submenu: to the **right** of the row that opened it, top edges aligned — the
 * direction its caret points (ENG-1920). Material's default for these was
 * `anchorOrigin: left / transformOrigin: right`, which put the flyout on the opposite side from
 * the arrow.
 */
export const NEXT_SUBMENU_PLACEMENT = {
  ...NEXT_MENU_TRANSITION,
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'top', horizontal: 'right' },
  transformOrigin: { vertical: 'top', horizontal: 'left' }
} as const
